import { Color, Spacing } from '@persona-id/design-tokens';
import { includes, remove } from 'lodash';
import { ChangeEvent, useMemo } from 'react';
import styled from 'styled-components';

import { AccountType } from 'dashboard/models/account-type';
import { DatumType, DatumTypeToken } from 'dashboard/models/graph';
import { NodeFunctionsSource } from 'dashboard/models/graph-backend';
import Button from 'design-system/components/Button';
import { CheckboxField } from 'design-system/components/FormField';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FieldSchemaType } from 'lib/fields';

const SUPPORTED_EXPORT_TYPES = [
  FieldSchemaType.Array,
  FieldSchemaType.Boolean,
  FieldSchemaType.Choices,
  FieldSchemaType.Date,
  FieldSchemaType.Datetime,
  FieldSchemaType.Hash,
  FieldSchemaType.Integer,
  FieldSchemaType.Json,
  FieldSchemaType.Number,
  FieldSchemaType.String,
];

const TooltipText = styled.span`
  border-bottom: 1px dotted #848484;
`;

export interface ExportConfigStruct {
  datumTypes: DatumTypeToken[];
  fieldsByAccountType: { [accountTypeToken: string]: string[] };
}

interface GraphExportConfigProps {
  onUpdateExportConfig: (exportConfig: ExportConfigStruct) => void;
  accountTypes: AccountType[];
  sources: NodeFunctionsSource[];
  exportConfig: ExportConfigStruct;
  datumTypes: DatumType[];
}

const Header = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  margin: ${Spacing[3]} 0;
`;

const HeaderActions = styled.div`
  display: flex;
  margin-left: auto;

  > :first-child {
    margin-right: ${Spacing[1]};
  }
`;

export const GraphExportConfig = ({
  onUpdateExportConfig,
  accountTypes,
  sources,
  datumTypes,
  exportConfig,
}: GraphExportConfigProps) => {
  const usableAccountTypes = useMemo(() => {
    const accountTypeTokens = sources
      .filter((source) => source['object-type'] === 'Account')
      .map((source) => source['object-type-token']);

    return accountTypes.filter((accountType) => {
      if (includes(accountTypeTokens, accountType.id)) {
        return (
          accountType.attributes['field-schemas'].filter((fieldSchema) =>
            includes(SUPPORTED_EXPORT_TYPES, fieldSchema.type),
          ).length > 0
        );
      }

      return false;
    });
  }, [sources, accountTypes]);

  const handleDeselectAll = () => {
    onUpdateExportConfig({ datumTypes: [], fieldsByAccountType: {} });
  };

  const handleSelectAll = () => {
    const fieldsConfig: { [accountTypeToken: string]: string[] } = {};
    usableAccountTypes.forEach((accountType) => {
      fieldsConfig[accountType.id] = accountType.attributes['field-schemas']
        .filter((fs) => includes(SUPPORTED_EXPORT_TYPES, fs.type))
        .map((fs) => fs.key);
    });

    onUpdateExportConfig({
      datumTypes: datumTypes.map((dt) => dt.token),
      fieldsByAccountType: fieldsConfig,
    });
  };

  const handleUpdateDatumTypeSection = (values: DatumTypeToken[]) => {
    onUpdateExportConfig({ ...exportConfig, datumTypes: values });
  };

  const handleUpdateFieldsByAccountTypeSection = (token: string, fieldValues: string[]) => {
    onUpdateExportConfig({
      ...exportConfig,
      fieldsByAccountType: { ...exportConfig.fieldsByAccountType, [token]: fieldValues },
    });
  };

  return (
    <>
      <Header>
        <Tooltip
          portal={false}
          interactive
          side='bottom'
          trigger={<TooltipText tabIndex={0}>Columns</TooltipText>}
        >
          Only selected columns will be included in the export
        </Tooltip>
        <HeaderActions>
          <Button
            id='GraphExportModalConfig__deselect-all-button'
            variant='basic'
            width='auto'
            onClick={handleDeselectAll}
          >
            Deselect all
          </Button>
          <Button
            id='GraphExportModalConfig__select-all-button'
            variant='basic'
            width='auto'
            onClick={handleSelectAll}
          >
            Select all
          </Button>
        </HeaderActions>
      </Header>
      <ConfigSection
        header='Nodes'
        options={datumTypes.map((datumType) => ({ label: datumType.name, value: datumType.token }))}
        values={exportConfig.datumTypes}
        onSelectOption={(values) => handleUpdateDatumTypeSection(values)}
      />
      <Spacer height={2} />

      {usableAccountTypes.map((accountType) => (
        <div key={`GraphExportModalConfig__section--${accountType.id}`}>
          <ConfigSection
            header={accountType.attributes.name}
            options={accountType.attributes['field-schemas']
              .filter((fieldSchema) => includes(SUPPORTED_EXPORT_TYPES, fieldSchema.type))
              .map((fieldSchema) => ({
                label: fieldSchema.label || fieldSchema.key,
                value: fieldSchema.key,
              }))}
            values={exportConfig.fieldsByAccountType[accountType.id] || []}
            onSelectOption={(values) =>
              handleUpdateFieldsByAccountTypeSection(accountType.id, values)
            }
          />
          <Spacer height={2} />
        </div>
      ))}
    </>
  );
};

interface ConfigSectionProps {
  header: string;
  options: {
    label: string;
    value: string;
  }[];
  values: string[];
  onSelectOption: (values: string[]) => void;
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto auto auto;
`;

const StyledHeader = styled(Heading)`
  border-bottom: 1px solid ${Color.BorderHairline};
`;

const ConfigSection = ({ header, options, values, onSelectOption }: ConfigSectionProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>, checkboxValue: string) => {
    if (e.target.checked) {
      onSelectOption([...values, checkboxValue]);
    } else {
      onSelectOption(remove(values, (val) => val !== checkboxValue));
    }
  };
  return (
    <>
      <StyledHeader size='sm'>{header}</StyledHeader>
      <Spacer height={2} />
      <GridContainer>
        {options.map((option) => (
          <CheckboxField
            checked={includes(values, option.value)}
            onChange={(e) => handleChange(e, option.value)}
            key={option.value}
            checkboxLabel={option.label}
            value={option.value}
          />
        ))}
      </GridContainer>
    </>
  );
};
